import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Statistic, Typography, Spin, Button } from 'antd';
import {
  TeamOutlined, FileTextOutlined, CheckCircleOutlined,
  ClockCircleOutlined, SolutionOutlined, ShopOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../api/client';

const { Title } = Typography;

const PIE_COLORS = ['#22c55e', '#6b7280', '#ef4444', '#f59e0b', '#3b82f6'];

interface Stats {
  total_users: number;
  total_prescriptions: number;
  active_prescriptions: number;
  dispensed_today: number;
  pending_doctors: number;
  pending_pharmacy: number;
}

interface DailyPoint { date: string; count: number; }

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [daily, setDaily] = useState<DailyPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback((isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/stats/daily'),
    ]).then(([s, d]) => {
      setStats(s.data);
      setDaily(d.data);
    }).finally(() => {
      setLoading(false);
      setRefreshing(false);
    });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  if (!stats) return null;

  const pieData = [
    { name: 'Активные',  value: stats.active_prescriptions },
    { name: 'Выданные',  value: stats.total_prescriptions - stats.active_prescriptions },
  ].filter(d => d.value > 0);

  const statCards = [
    { title: 'Пользователей',          value: stats.total_users,           icon: <TeamOutlined />,         color: '#3b82f6' },
    { title: 'Всего рецептов',         value: stats.total_prescriptions,   icon: <FileTextOutlined />,     color: '#8b5cf6' },
    { title: 'Активных рецептов',      value: stats.active_prescriptions,  icon: <CheckCircleOutlined />,  color: '#22c55e' },
    { title: 'Выдано сегодня',         value: stats.dispensed_today,       icon: <ClockCircleOutlined />,  color: '#f59e0b' },
    { title: 'Врачей на проверке',     value: stats.pending_doctors,       icon: <SolutionOutlined />,     color: '#ef4444' },
    { title: 'Аптек на проверке',      value: stats.pending_pharmacy,      icon: <ShopOutlined />,         color: '#f97316' },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>Дашборд</Title>
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={() => fetchData(true)}
        >
          Обновить
        </Button>
      </div>

      {}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {statCards.map((c) => (
          <Col xs={24} sm={12} lg={8} xl={4} key={c.title}>
            <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <Statistic
                title={c.title}
                value={c.value}
                prefix={React.cloneElement(c.icon, { style: { color: c.color } })}
                valueStyle={{ color: c.color, fontWeight: 700 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Рецепты по дням (7 дней)" bordered={false}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }}
                  tickFormatter={(v) => v.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip labelFormatter={(v) => `Дата: ${v}`}
                  formatter={(v: any) => [v, 'Рецептов']} />
                <Line type="monotone" dataKey="count" stroke="#3b82f6"
                  strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Статусы рецептов" bordered={false}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', height: '100%' }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={90}
                    dataKey="value" label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 80 }}>
                Нет данных
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
